import React from 'react';
import { useForm } from 'react-hook-form';
import { BlankModal, ButtonGroup, ModalBody, ModalFooter, ModalHeader } from '@cad-arc';
import { DispositionsForm, ClearEventFormType, EventPanelOptions } from '@mark43/cad-api';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { RefreshEventPanel } from './refresh-event-panel';
import { ConstrainedModalProps } from '../modals/types';
import { SetActiveEventPanelTab } from './active-event-panel-tab-provider';
import { useDispositionQuery } from '../dispositions/hooks/use-disposition-query';
import { DispositionForm } from '../dispositions/components/disposition-form';
import { CLEAR_EVENT_DEFAULT_FORM_VALUES } from '../dispositions/constants';
import { useClearEventMutation } from '../dispositions/hooks/use-clear-event-mutation';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';

export type ClearEventModalComponentType = typeof ClearEventModal;
export function ClearEventModal(
    props: {
        eventId: number;
        eventPanelOptions: EventPanelOptions;
        refreshEventPanel: RefreshEventPanel;
        setActiveEventPanelTab: SetActiveEventPanelTab;
        agencyId: number;
    } & ConstrainedModalProps
): React.ReactElement {
    const { unitLevelDispositionsEnabled } = useEnabledFeatures();
    const modalControls = useFullscreenModalControls();
    const onClose = props.onClose ?? modalControls.close;

    const dispositionQuery = useDispositionQuery({
        agencyEventId: props.eventId,
    });
    const { isFetching, data } = dispositionQuery;

    const clearEventMutation = useClearEventMutation({
        onMutationSuccess: (data) => {
            const eventPanelView = data.data.data?.eventPanelView;
            if (eventPanelView) {
                props.refreshEventPanel({ eventPanelView });
            }
            onClose();
            if (!unitLevelDispositionsEnabled) {
                props.setActiveEventPanelTab('DISPOSITIONS');
            }
        },
    });

    const clearEventForm = useForm<ClearEventFormType>({
        defaultValues: {
            ...CLEAR_EVENT_DEFAULT_FORM_VALUES,
            eventId: props.eventId,
            updateDispositionsRequest: unitLevelDispositionsEnabled
                ? {
                      form: {
                          ...data?.form,
                          requestingUnitId: undefined,
                          isRequestingUnitPrimaryUnit: undefined,
                      },
                  }
                : undefined,
        },
    });

    const dispositionFormInitialValues: DispositionsForm = {
        ...data?.form,
        agencyEventId: props.eventId,
        additionalDispositions: data?.form.additionalDispositions ?? [],
    };

    return (
        <IntegratedFormProvider
            query={dispositionQuery}
            mutation={clearEventMutation}
            form={clearEventForm}
        >
            <BlankModal
                isOpen={true}
                onClose={onClose}
                portalProps={props.portalProps}
                constrainToPortal={true}
                closeOnOverlayClick={props.closeOnOverlayClick}
            >
                <ModalHeader>Clear Event</ModalHeader>
                <ModalBody>
                    <FormBody>
                        <DispositionForm
                            isEventClearing
                            isLoading={isFetching}
                            agencyId={props.agencyId}
                            dispositionOptions={props.eventPanelOptions.dispositionOptions}
                            initialValues={dispositionFormInitialValues}
                        />
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <ButtonGroup>
                        <FormCancelButton
                            onClick={() => {
                                onClose();
                            }}
                        />
                        <FormSaveButton>Clear</FormSaveButton>
                    </ButtonGroup>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
}
